import { FC, ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

export type DialogSize = 'md' | 'lg';

export type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  /** The action row, rendered right-aligned at the foot of the panel. */
  actions?: ReactNode;
  /**
   * A control rendered on the title row, right of the heading. For a panel that
   * would otherwise spend a whole row on a single dismiss button.
   */
  headerAction?: ReactNode;
  tone?: 'default' | 'danger';
  /** The panel's width: md (default) is the standard 400px, lg a wider body. */
  size?: DialogSize;
  /** Placement only: the panel's frame and spacing are the dialog's. */
  className?: string;
};

/**
 * A controlled dialog.
 *
 * Rendering is decided by the caller's `open`; this component only reports
 * closes back through `onOpenChange`. Escape and a backdrop click request a
 * close, focus moves into the panel on open and returns to the trigger on
 * close, and body scroll is locked for as long as the panel is open.
 */
const Dialog: FC<DialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  actions,
  headerAction,
  tone = 'default',
  size = 'md',
  className,
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus and scroll are tied to visibility alone, so a caller that passes a
  // fresh onOpenChange on every render does not make the panel refocus.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="ui-dialog-backdrop"
      // Decorative: click-outside-to-close only. Escape and focus are handled by
      // the dialog itself, so this needs no keyboard handler of its own.
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onOpenChange(false);
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={['ui-dialog', size === 'lg' && 'ui-dialog--lg', tone === 'danger' && 'ui-dialog--danger', className]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="ui-dialog-header">
          <div className="ui-dialog-heading">
            <h2 className="ui-dialog-title" id={titleId}>
              {title}
            </h2>
            {description && (
              <p className="ui-dialog-description" id={descriptionId}>
                {description}
              </p>
            )}
          </div>
          {headerAction}
        </div>
        {children}
        {actions && <div className="ui-dialog-actions">{actions}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Dialog;
